"use client";

import { useId, useMemo, useState } from "react";
import Link from "next/link";
import { MoveRight, RotateCcw, UserRound } from "lucide-react";

import { cn } from "@/lib/utils";
import {
  TUTORS,
  TUTOR_FOCUS,
  TUTOR_BEST_FOR,
  TUTOR_INDUSTRIES,
} from "@/lib/tutors";
import { LocalTutorsMap } from "@/components/marketing/local-tutors-map";

interface FilterSelectProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: readonly string[];
  anyLabel: string;
}

function FilterSelect({
  label,
  value,
  onChange,
  options,
  anyLabel,
}: FilterSelectProps) {
  const id = useId();
  return (
    <div>
      <label
        htmlFor={id}
        className="block text-xs font-semibold uppercase tracking-[0.12em] text-mid"
      >
        {label}
      </label>
      <select
        id={id}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="mt-1.5 w-full rounded-md border border-line bg-paper px-3 py-2 text-sm text-dark shadow-sm focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-secondary"
      >
        <option value="">{anyLabel}</option>
        {options.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </select>
    </div>
  );
}

function TagRow({ label, items }: { label: string; items: readonly string[] }) {
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <span className="text-[11px] font-semibold uppercase tracking-wide text-mid">
        {label}
      </span>
      {items.map((item) => (
        <span
          key={item}
          className="rounded-full border border-line bg-light-bg px-2 py-0.5 text-[11px] text-dark"
        >
          {item}
        </span>
      ))}
    </div>
  );
}

interface OneToOneFinderProps {
  className?: string;
  /** Where the "can't see a fit" prompt sends people. */
  enquiryHref?: string;
}

/**
 * A filterable finder for one-to-one (and small-group) AI tutoring: pick what
 * you want to learn, who it is for, and your sector, and the matching local
 * tutors are listed and spotlighted on the map. Faces stay blurred (a privacy
 * placeholder); each tutor links to that area's local-training page.
 */
export function OneToOneFinder({
  className,
  enquiryHref = "#enquiry",
}: OneToOneFinderProps) {
  const [focus, setFocus] = useState("");
  const [bestFor, setBestFor] = useState("");
  const [industry, setIndustry] = useState("");

  const active = Boolean(focus || bestFor || industry);

  const matches = useMemo(
    () =>
      TUTORS.filter(
        (t) =>
          (!focus || (t.focus as readonly string[]).includes(focus)) &&
          (!bestFor || (t.bestFor as readonly string[]).includes(bestFor)) &&
          (!industry ||
            (t.industries as readonly string[]).includes(industry)),
      ),
    [focus, bestFor, industry],
  );

  const highlightHrefs = active ? matches.map((m) => m.href) : null;

  const clear = () => {
    setFocus("");
    setBestFor("");
    setIndustry("");
  };

  return (
    <div className={cn("grid gap-8 lg:grid-cols-[1fr_minmax(0,360px)]", className)}>
      {/* Filters + matching tutors */}
      <div>
        <div className="grid gap-4 sm:grid-cols-3">
          <FilterSelect
            label="What to learn"
            value={focus}
            onChange={setFocus}
            options={TUTOR_FOCUS}
            anyLabel="Any focus"
          />
          <FilterSelect
            label="Best for"
            value={bestFor}
            onChange={setBestFor}
            options={TUTOR_BEST_FOR}
            anyLabel="Anyone"
          />
          <FilterSelect
            label="Your sector"
            value={industry}
            onChange={setIndustry}
            options={TUTOR_INDUSTRIES}
            anyLabel="Any sector"
          />
        </div>

        <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
          <p className="text-sm text-mid" role="status" aria-live="polite">
            <strong className="font-semibold text-dark">{matches.length}</strong>{" "}
            {matches.length === 1 ? "tutor" : "tutors"}{" "}
            {active ? "match your filters" : "across the South West and South"}
          </p>
          {active && (
            <button
              type="button"
              onClick={clear}
              className="inline-flex items-center gap-1.5 text-sm font-medium text-primary underline-offset-2 hover:text-secondary hover:underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-secondary"
            >
              <RotateCcw className="size-3.5" aria-hidden="true" />
              Clear filters
            </button>
          )}
        </div>

        {matches.length > 0 ? (
          <ul className="mt-6 grid gap-4 sm:grid-cols-2" role="list">
            {matches.map((t) => (
              <li key={t.href}>
                <Link
                  href={t.href}
                  className="group flex h-full flex-col rounded-md border border-line bg-paper p-5 shadow-sm transition-colors hover:border-secondary/50"
                >
                  <div className="flex items-center gap-3">
                    {/* Silhouette placeholder — no real photos are shown. */}
                    <span className="flex size-11 shrink-0 items-center justify-center rounded-full border border-paper bg-primary/15 text-primary shadow ring-1 ring-primary/30">
                      <UserRound className="size-5" aria-hidden="true" />
                    </span>
                    <div>
                      <p className="font-heading font-semibold text-dark">
                        {t.town}
                      </p>
                      <p className="text-xs text-mid">{t.county}</p>
                    </div>
                  </div>
                  <div className="mt-4 space-y-2.5">
                    <TagRow label="Teaches" items={t.focus} />
                    <TagRow label="Best for" items={t.bestFor} />
                    <TagRow label="Sectors" items={t.industries} />
                  </div>
                  <span className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-primary group-hover:text-secondary">
                    AI training in {t.town}
                    <MoveRight className="size-4" aria-hidden="true" />
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        ) : (
          <div className="mt-6 rounded-md border border-dashed border-line bg-light-bg p-6 text-sm text-mid">
            <p>
              No tutor matches that exact combination yet. Our tutors travel to
              nearby towns and also teach one-to-one over video, so widen a
              filter or{" "}
              <Link
                href={enquiryHref}
                className="font-semibold text-primary underline underline-offset-2 hover:text-secondary"
              >
                tell us what you need
              </Link>{" "}
              and we will match you.
            </p>
            <button
              type="button"
              onClick={clear}
              className="mt-3 inline-flex items-center gap-1.5 text-sm font-medium text-primary underline-offset-2 hover:text-secondary hover:underline"
            >
              <RotateCcw className="size-3.5" aria-hidden="true" />
              Clear filters
            </button>
          </div>
        )}

        <p className="mt-6 text-sm text-mid">
          Can&rsquo;t see the right fit?{" "}
          <Link
            href={enquiryHref}
            className="font-semibold text-primary underline underline-offset-2 hover:text-secondary"
          >
            Enquire about one-to-one tutoring
          </Link>{" "}
          and we will pair you with the right tutor, in person or remotely.
        </p>
      </div>

      {/* Map (spotlights the filtered tutors) */}
      <div className="lg:sticky lg:top-24 lg:self-start">
        <LocalTutorsMap highlightHrefs={highlightHrefs} />
        <p className="mt-3 text-center text-xs text-mid">
          Tutors are shown as placeholders while we confirm availability. Most
          also teach remotely, UK-wide.
        </p>
      </div>
    </div>
  );
}
